import { useInvalidate, useUpdate } from '@refinedev/core';
import { Button, Flex, Typography } from 'antd';
import { ROUTES } from 'common/constants';
import { ItemsIcon, PersonAddIcon, WhatsappIcon } from 'common/icons';
import { useNavigateWithOrgAndFarm } from 'common/utils';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/auth';
import { Organization } from 'src/pages/organization/organization.model';

import { ActionsList } from '../actions-list/actions-list.component';
import { OnboardingAction } from '../actions-list/actions-list.model';
import {
  getAccountMembersUrl,
  getAccountWorkspacesUrl,
} from '../actions-list/actions-list.util';
import * as Styled from './onboarding-finish.style';

const { Title, Paragraph } = Typography;

interface OnboardingFinishViewProps {
  organization: Organization | null;
  closeModal?: () => void;
}

const OnboardingFinishView = ({
  organization,
  closeModal,
}: OnboardingFinishViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithOrgAndFarm();
  const { user } = useAuth();
  const invalidate = useInvalidate();

  const userName = user?.first_name || user?.name || t('common.user');

  const { mutate, isPending } = useUpdate();

  const handleComplete = (onSuccess?: () => void) => {
    mutate(
      {
        resource: `onboarding/organization`,
        id: organization?.id || '',
        values: {},
        meta: {
          specifier: 'complete',
        },
        successNotification: false,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'organization-user',
            invalidates: ['resourceAll'],
          });
          onSuccess?.();
        },
      },
    );
  };

  const handleFinish = useCallback(() => {
    handleComplete(() => {
      closeModal?.();
    });
  }, [closeModal]);

  const actions: OnboardingAction[] = [
    {
      id: 'invite',
      label: t('onboarding.action.invite'),
      icon: <PersonAddIcon />,
      onClick: () =>
        window.open(
          getAccountMembersUrl(
            organization?.workspace_id || '',
            organization?.id || '',
          ),
          '_blank',
        ),
    },
    {
      id: 'item_import',
      label: t('onboarding.action.item_import'),
      icon: <ItemsIcon />,
      onClick: () => {
        handleComplete(() =>
          navigate(ROUTES.IMPORT, organization?.id || '', null),
        );
      },
    },
    {
      id: 'voice_management',
      label: t('onboarding.action.voice_management'),
      icon: <WhatsappIcon />,
      onClick: () => {
        handleComplete(() =>
          navigate(ROUTES.DASHBOARD, organization?.id || '', null, {
            'balanceAi[open]': 'true',
          }),
        );
      },
    },
  ];

  return (
    <Styled.FinishWrapper>
      <Styled.SceneImage
        style={{
          backgroundImage: `url('/assets/onboarding-success-scene.gif')`,
        }}
      />
      <Styled.ContentLayout>
        <Styled.LeftContent>
          <Title level={4}>
            {t('onboarding.finish.title', { name: userName })}
          </Title>
          <Paragraph>{t('onboarding.finish.subtitle')}</Paragraph>
          <Paragraph>{t('onboarding.finish.description')}</Paragraph>
        </Styled.LeftContent>

        <Styled.RightContent>
          <ActionsList actions={actions} />
          <Flex gap="small" justify="flex-end">
            <Button
              size="large"
              onClick={() =>
                window.open(
                  getAccountWorkspacesUrl(
                    organization?.workspace_id || '',
                    organization?.id || '',
                  ),
                  '_blank',
                )
              }
            >
              {t('onboarding.start.buttons.goToAccount')}
            </Button>
            <Button
              size="large"
              type="primary"
              onClick={handleFinish}
              loading={isPending}
            >
              {t('buttons.finish')}
            </Button>
          </Flex>
        </Styled.RightContent>
      </Styled.ContentLayout>
    </Styled.FinishWrapper>
  );
};

export { OnboardingFinishView };
